import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import {
    PDF_PREVIEW_ERROR,
    PDF_PREVIEW_MESSAGE_TYPE,
    PDF_PREVIEW_READY,
} from '../utils/pdfPreview';

pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs';

const renderPdfToContainer = async (pdfBytes, container) => {
    const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
    const pdf = await loadingTask.promise;

    container.replaceChildren();

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.35 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto 12px';
        canvas.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';

        container.appendChild(canvas);
        await page.render({ canvasContext: context, viewport }).promise;
    }
};

const PdfPreview = () => {
    const [searchParams] = useSearchParams();
    const previewId = searchParams.get('id');
    const containerRef = useRef(null);
    const [status, setStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');

    const handlePrint = () => {
        window.print();
    };

    useEffect(() => {
        if (!previewId) {
            setErrorMessage('미리보기 ID가 없습니다.');
            setStatus('error');
            return undefined;
        }

        const handleMessage = async (event) => {
            if (event.origin !== window.location.origin) return;
            const { data } = event;
            if (!data || data.previewId !== previewId) return;

            if (data.type === PDF_PREVIEW_MESSAGE_TYPE) {
                const bytes = new Uint8Array(data.pdfBytes);
                document.title = data.fileName || 'document.pdf';

                try {
                    if (!containerRef.current) {
                        throw new Error('PDF 미리보기 영역을 찾을 수 없습니다.');
                    }

                    await renderPdfToContainer(bytes, containerRef.current);
                    setStatus('ready');
                } catch (error) {
                    console.error('PDF 렌더링 실패:', error);
                    setErrorMessage(error.message || 'PDF를 표시할 수 없습니다.');
                    setStatus('error');
                }
            }

            if (data.type === PDF_PREVIEW_ERROR) {
                setErrorMessage(data.message || 'PDF 생성 중 오류가 발생했습니다.');
                setStatus('error');
            }
        };

        window.addEventListener('message', handleMessage);

        if (window.opener) {
            window.opener.postMessage(
                { type: PDF_PREVIEW_READY, previewId },
                window.location.origin
            );
        }

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [previewId]);

    if (status === 'error') {
        return (
            <div style={{
                margin: 0,
                padding: 24,
                minHeight: '100vh',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                color: '#b42318',
                background: '#fff',
                whiteSpace: 'pre-wrap',
            }}
            >
                {errorMessage}
            </div>
        );
    }

    return (
        <div className="pdf-preview-root" style={{ minHeight: '100vh', background: '#525659' }}>
            <style>
                {`
                    @media print {
                        .pdf-preview-toolbar { display: none !important; }
                        .pdf-preview-root { background: #fff !important; }
                        .pdf-preview-pages { padding: 0 !important; }
                        .pdf-preview-pages canvas {
                            box-shadow: none !important;
                            margin: 0 auto !important;
                            page-break-after: always;
                            max-width: 100%;
                            height: auto !important;
                        }
                    }
                `}
            </style>
            {status === 'ready' && (
                <div
                    className="pdf-preview-toolbar"
                    style={{
                        position: 'fixed',
                        top: 16,
                        right: 16,
                        zIndex: 10,
                    }}
                >
                    <button
                        type="button"
                        onClick={handlePrint}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 16px',
                            border: 'none',
                            borderRadius: 8,
                            background: '#fff',
                            color: '#1f2937',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        }}
                    >
                        <Printer size={18} />
                        인쇄
                    </button>
                </div>
            )}
            {status === 'loading' && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: '#f5f5f5',
                }}
                >
                    PDF를 생성하는 중입니다...
                </div>
            )}
            <div
                ref={containerRef}
                className="pdf-preview-pages"
                style={{
                    padding: status === 'ready' ? '16px 0' : 0,
                    minHeight: status === 'ready' ? '100vh' : 0,
                }}
            />
        </div>
    );
};

export default PdfPreview;
